<template>
  <div class="team-members q-pa-sm q-px-md">
    <h2 class="team-members__title">Teams</h2>
    <p class="team-members__subtitle">Manage your team and their permissions</p>

    <!-- Loading state indicator -->
    <div v-if="isLoading" class="team-members__loading">
      <q-spinner-dots color="primary" size="40px" />
    </div>

    <div v-else>
      <div class="team-members__list">
        <!-- Current user (always shown) -->
        <current-user-item :user="user" :display-role="currentUserDisplayRole" :badge-color="currentUserBadgeColor" />

        <!-- Scrollable container for team members -->
        <div class="team-members__scrollable-container" :class="{ 'team-members__scrollable-container--scrollable': shouldShowScroll }">
          <!-- Other team members -->
          <team-member-item v-for="member in teamMembers" :key="member.id" :member="member" :can-manage="canManageTeamMembers" @edit="handleEditUser" @remove="confirmRemoveMember" />

          <!-- Empty state if no other members -->
          <empty-state v-if="teamMembers.length === 0" />
        </div>

        <!-- Add team member button (only for admins) -->
        <div v-if="canManageTeamMembers" class="text-center q-mt-md">
          <q-btn flat class="team-members__add-btn" size="sm" @click="handleInviteUser">
            <q-icon name="add" size="xs" class="q-mr-xs" />
            <span>Add team member</span>
          </q-btn>
        </div>
      </div>
    </div>

    <!-- Add/Edit Team Member Dialog -->
    <team-member-form-dialog v-model="showDialog" :editing-member="editingMember" :is-requesting="isRequesting" @save="saveMember" ref="memberForm" />

    <!-- Confirmation Dialog for removing members -->
    <confirmation-dialog v-model="showConfirmDialog" :is-requesting="isRequesting" @confirm="removeMember" />
  </div>
</template>

<script>
import { ref, computed } from '@vue/composition-api';
import { useQuery, useMutation, useQueryClient } from '@tanstack/vue-query';
import { mapGetters } from 'vuex';
import { UserApi } from '@api/index';
import CurrentUserItem from './TeamMembers/CurrentUserItem.vue';
import TeamMemberItem from './TeamMembers/TeamMemberItem.vue';
import EmptyState from './TeamMembers/EmptyState.vue';
import TeamMemberFormDialog from './TeamMembers/TeamMemberFormDialog.vue';
import ConfirmationDialog from './TeamMembers/ConfirmationDialog.vue';
import { checkTeamMemberLimit } from '@/utils/resourceMetrics';

export default {
  name: 'TeamMembers',

  components: {
    CurrentUserItem,
    TeamMemberItem,
    EmptyState,
    TeamMemberFormDialog,
    ConfirmationDialog
  },

  setup(props, { root }) {
    const queryClient = useQueryClient();

    // Reactive data
    const showDialog = ref(false);
    const showConfirmDialog = ref(false);
    const editingMember = ref(null);
    const memberToRemove = ref(null);
    const registration_guid = ref(null);
    const businessData = ref(null);
    const isRequesting = ref(false);

    // Get team member payload for API requests
    const getTeamMemberPayload = () => ({
      filters: {
        role: 'subClient',
        statuses: ['active', 'blocked', 'deleted', 'waiting']
      }
    });

    // Normalize team member data to ensure consistent role format
    const normalizeTeamMembers = members => {
      if (!Array.isArray(members)) return [];
      return members.map(member => ({
        ...member,
        subClientRole: member.subClientRole ? String(member.subClientRole).toLowerCase() : 'viewer'
      }));
    };

    // TanStack Query for team members
    const { data: teamMembersData, isLoading } = useQuery({
      queryKey: ['team-members'],
      queryFn: async () => {
        const payload = getTeamMemberPayload();
        const response = await UserApi.searchUsers({ payload });
        const members = response.statusCode === 200 ? response.data.users : [];
        return normalizeTeamMembers(members);
      },
      staleTime: 1000 * 60 * 2, // 2 minutes
      cacheTime: 1000 * 60 * 10, // 10 minutes
      onError: error => {
        console.error('Error fetching team members:', error);
        root.$q.notify({
          type: 'negative',
          message: 'Failed to load team members. Please try again later.'
        });
      }
    });

    const teamMembers = computed(() => teamMembersData.value || []);

    // Mutation for updating team member
    const updateTeamMemberMutation = useMutation({
      mutationFn: async ({ id, userData, roleValue }) => {
        const payload = {
          ...editingMember.value,
          role: 'client',
          name: `${userData.firstName} ${userData.lastName}`.trim(),
          contact_name: `${userData.firstName} ${userData.lastName}`.trim(),
          phone: userData.phoneNumber.replace(/\s/g, ''),
          contact_phone: userData.phoneNumber.replace(/\s/g, '')
        };

        const response = await UserApi.updateUser({ id, payload });

        // Update role if it changed
        if (response.statusCode === 200 && roleValue !== editingMember.value.subClientRole) {
          await UserApi.updateSubClientRole({
            id: editingMember.value.id,
            payload: { role: roleValue }
          });
        }

        return { response, userData, roleValue, id };
      },
      onMutate: async variables => {
        // Cancel any outgoing refetches to prevent overwriting optimistic update
        await queryClient.cancelQueries({ queryKey: ['team-members'] });

        // Snapshot the previous value for rollback
        const previousTeamMembers = queryClient.getQueryData(['team-members']);

        // Optimistically update the cache immediately
        queryClient.setQueryData(['team-members'], old => {
          if (!old) return old;
          return old.map(member => {
            if (member.id === variables.id) {
              return {
                ...member,
                name: `${variables.userData.firstName} ${variables.userData.lastName}`.trim(),
                first_name: variables.userData.firstName,
                last_name: variables.userData.lastName,
                phone: variables.userData.phoneNumber.replace(/\s/g, ''),
                contact_phone: variables.userData.phoneNumber.replace(/\s/g, ''),
                subClientRole: variables.roleValue
              };
            }
            return member;
          });
        });

        return { previousTeamMembers };
      },
      onError: (err, variables, context) => {
        // Rollback on error
        if (context?.previousTeamMembers) {
          queryClient.setQueryData(['team-members'], context.previousTeamMembers);
        }
        root.$q.notify({
          type: 'negative',
          position: 'top',
          message: 'Failed to update team member. Please try again later.'
        });
      },
      onSuccess: () => {
        root.$q.notify({
          type: 'positive',
          position: 'top',
          message: 'Team member updated successfully'
        });
        showDialog.value = false;

        // Invalidate queries in background to ensure server sync
        setTimeout(() => {
          queryClient.invalidateQueries({ queryKey: ['team-members'] });
        }, 1000);
      },
      onSettled: () => {
        isRequesting.value = false;
      }
    });

    // Mutation for creating team member
    const createTeamMemberMutation = useMutation({
      mutationFn: async ({ userData, roleValue }) => {
        const formattedPhone = userData.phoneNumber.replace(/\s/g, '');
        const payload = {
          additionalInformations: {
            company_name: businessData.value?.company_name || 'Default Company',
            industry: businessData.value?.industry || 'Technology',
            contact_name: userData.firstName,
            contact_surname: userData.lastName,
            contact_email: userData.email,
            contact_phone: formattedPhone,
            main_location: businessData.value?.main_location || '',
            country: { value: businessData.value?.country || 'US' }
          },
          password: generatePassword(12),
          user: {
            name: userData.firstName,
            surname: userData.lastName,
            email: userData.email,
            phone: formattedPhone
          },
          paymentDetail: {
            type: 'test',
            details: {}
          }
        };

        return await UserApi.createBusiness(payload, registration_guid.value, false, roleValue);
      },
      onSuccess: () => {
        root.$q.notify({
          type: 'positive',
          position: 'top',
          message: 'Team member added successfully'
        });
        showDialog.value = false;

        // Invalidate and refetch team members
        queryClient.invalidateQueries({ queryKey: ['team-members'] });
      },
      onError: () => {
        root.$q.notify({
          type: 'negative',
          position: 'top',
          message: 'Failed to add team member. Please try again later.'
        });
      },
      onSettled: () => {
        isRequesting.value = false;
      }
    });

    // Mutation for deleting team member
    const deleteTeamMemberMutation = useMutation({
      mutationFn: async memberId => {
        return await UserApi.deleteSubClient(memberId);
      },
      onMutate: async memberId => {
        // Cancel any outgoing refetches
        await queryClient.cancelQueries({ queryKey: ['team-members'] });

        // Snapshot the previous value
        const previousTeamMembers = queryClient.getQueryData(['team-members']);

        // Optimistically remove the member
        queryClient.setQueryData(['team-members'], old => {
          if (!old) return old;
          return old.filter(member => member.id !== memberId);
        });

        return { previousTeamMembers };
      },
      onError: (err, memberId, context) => {
        // Rollback on error
        if (context?.previousTeamMembers) {
          queryClient.setQueryData(['team-members'], context.previousTeamMembers);
        }
        root.$q.notify({
          type: 'negative',
          position: 'top',
          message: 'Failed to remove team member. Please try again later.'
        });
      },
      onSuccess: () => {
        root.$q.notify({
          type: 'positive',
          position: 'top',
          message: 'Team member removed successfully'
        });
        showConfirmDialog.value = false;
        memberToRemove.value = null;
      },
      onSettled: () => {
        isRequesting.value = false;
      }
    });

    // Helper function for password generation
    const generatePassword = length => {
      const charset = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*()_-+=<>?';
      let password = '';
      const passwordChecker = new RegExp('(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[^A-Za-z0-9])(?=.{8,})');

      do {
        password = '';
        for (let i = 0; i < length; i++) {
          const randomIndex = Math.floor(Math.random() * charset.length);
          password += charset[randomIndex];
        }
      } while (!passwordChecker.test(password));

      return password;
    };

    // Handler methods that need access to refs
    const handleInviteUser = () => {
      editingMember.value = null;
      showDialog.value = true;
    };

    const handleEditUser = member => {
      editingMember.value = member;
      showDialog.value = true;
    };

    const confirmRemoveMember = member => {
      memberToRemove.value = member;
      showConfirmDialog.value = true;
    };

    // Extract and normalize role value from user data
    const extractRoleValue = userData => {
      let roleValue = 'viewer'; // Safe default

      if (typeof userData.role === 'string') {
        roleValue = userData.role.toLowerCase();
      } else if (userData.role && userData.role.value) {
        roleValue = userData.role.value.toLowerCase();
      }

      if (roleValue === 'admin') {
        return 'manager';
      } else {
        return 'viewer'; // UI User becomes backend viewer
      }
    };

    // Save or update a team member
    const saveMember = async userData => {
      if (!editingMember.value) {
        // Use our utility to check member limits
        const memberCheck = checkTeamMemberLimit();

        if (!memberCheck.allowed) {
          root.$q.notify({
            type: 'negative',
            position: 'top',
            message: `Unable to add team member: You have reached the maximum limit of ${memberCheck.maxMembers} members for your current plan.`,
            timeout: 8000
          });
          return;
        }
      }

      isRequesting.value = true;
      const roleValue = extractRoleValue(userData);

      try {
        if (editingMember.value) {
          console.log('Updating team member:', editingMember.value.id, 'with data:', userData, 'role:', roleValue);

          // Update existing team member using TanStack mutation
          await updateTeamMemberMutation.mutateAsync({
            id: editingMember.value.id,
            userData,
            roleValue
          });

          console.log('Team member update completed successfully');

          // Update user data in store for member count
          await refreshUserData(teamMembers.value.length);
        } else {
          console.log('Creating new team member with data:', userData, 'role:', roleValue);

          // Create new team member using TanStack mutation
          await createTeamMemberMutation.mutateAsync({
            userData,
            roleValue
          });

          console.log('Team member creation completed successfully');

          // Update user data in store for incremented member count
          await refreshUserData(teamMembers.value.length + 1);
        }
      } catch (error) {
        // Error handling is done in the mutation's onError callback
        console.error('Error saving team member:', error);
      }
    };

    // Remove a team member
    const removeMember = async () => {
      if (!memberToRemove.value) return;

      isRequesting.value = true;

      try {
        // Use TanStack mutation for deleting team member
        await deleteTeamMemberMutation.mutateAsync(memberToRemove.value.id);

        // Refresh user data to update team member counts in UserDropdown
        await refreshUserData();
      } catch (error) {
        // Error handling is done in the mutation's onError callback
        console.error('Error removing team member:', error);
      }
    };

    // Refresh user data in store to update member counts
    const refreshUserData = async (memberCount = null) => {
      try {
        // Create a deep copy of the current user to ensure proper reactivity
        const currentUser = JSON.parse(JSON.stringify(root.$store.getters['user/getUser']));

        // Use provided count or fall back to current array length
        const actualMemberCount = memberCount !== null ? memberCount : teamMembers.value.length;

        // Update the total_members count in the user's plan
        if (currentUser.plan) {
          currentUser.plan.total_members = actualMemberCount;
        }
        root.$store.dispatch('user/saveUser', currentUser);
      } catch (error) {
        console.error('Error updating user data in store:', error);
      }
    };

    return {
      queryClient,
      showDialog,
      showConfirmDialog,
      editingMember,
      memberToRemove,
      registration_guid,
      businessData,
      isRequesting,
      teamMembers,
      isLoading,
      getTeamMemberPayload,
      normalizeTeamMembers,
      updateTeamMemberMutation,
      createTeamMemberMutation,
      deleteTeamMemberMutation,
      generatePassword,
      handleInviteUser,
      handleEditUser,
      confirmRemoveMember,
      extractRoleValue,
      saveMember,
      removeMember,
      refreshUserData
    };
  },

  data() {
    return {
      refreshTimeoutId: null
    };
  },

  computed: {
    ...mapGetters({
      user: 'user/getUser',
      maxMembers: 'resources/maxMembers',
      totalMembers: 'resources/totalMembers',
      memberCount: 'resources/memberCount'
    }),

    /**
     * Get the current user's role from sub_client_role
     * @returns {string} - 'manager', 'admin', or 'viewer'
     */
    currentUserRole() {
      return this.user?.sub_client_role || 'viewer';
    },

    /**
     * Check if the current user has admin privileges (manager or admin)
     * Manager and admin are treated the same - both are "Admin"
     * @returns {boolean} - True if user has admin privileges
     */
    isCurrentUserAdmin() {
      const role = this.currentUserRole;
      return role === 'manager' || role === 'admin';
    },

    /**
     * Check if the current user is a viewer (regular user)
     * @returns {boolean} - True if user is viewer
     */
    isCurrentUserViewer() {
      return this.currentUserRole === 'viewer';
    },

    /**
     * Check if the current user has permissions to manage team members
     * @returns {boolean} - True if user can add/edit/delete members (admin privileges)
     */
    canManageTeamMembers() {
      return this.isCurrentUserAdmin;
    },

    /**
     * Get the display role for the current user
     * Both manager and admin show as "Admin" in UI
     * @returns {string} - 'Admin' or 'User'
     */
    currentUserDisplayRole() {
      return this.isCurrentUserAdmin ? 'Admin' : 'User';
    },

    /**
     * Get the badge color for the current user's role
     * Both manager and admin use primary color
     * @returns {string} - Color for the role badge
     */
    currentUserBadgeColor() {
      return this.isCurrentUserAdmin ? 'primary' : 'grey-3';
    },

    /**
     * Check if the user has reached their member limit
     * @returns {boolean} - True if the limit is reached
     */
    isMemberLimitReached() {
      // Only check for new members, not for editing existing ones
      if (this.editingMember) return false;

      // Use our utility function to check member limits
      const memberCheck = checkTeamMemberLimit();
      return !memberCheck.allowed;
    },

    /**
     * Get the maximum number of members allowed by the plan
     * @returns {string|number} - The maximum number of members or 'Unlimited'
     */
    maxMembersAllowed() {
      if (!this.user || !this.user.plan || !this.user.plan.plans) {
        return 'Unknown';
      }

      const maxMembers = this.user.plan.plans.max_members;
      return maxMembers === null ? 'Unlimited' : maxMembers;
    },

    /**
     * Determine if scroll should be shown for team members list
     * @returns {boolean} - True if there are more than 3 team members
     */
    shouldShowScroll() {
      return this.teamMembers.length > 3;
    }
  },

  async mounted() {
    await this.fetchBusinessData();
  },

  methods: {
    async fetchBusinessData() {
      try {
        const data = await UserApi.getUser({ id: this.user.id, role: 'client' });
        if (data.statusCode === 200 && data.user) {
          this.registration_guid = data.user.registration_guid;

          if (this.registration_guid) {
            const { statusCode, data: businessData } = await UserApi.getBusinessByGuid(this.registration_guid);
            if (statusCode === 200 && businessData && businessData.user) {
              this.businessData = businessData.user;
            }
          }
        }
      } catch (error) {
        console.error('Error fetching user details:', error);
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.team-members {
  width: 100%;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  text-align: left;
  overflow: hidden;
  position: relative; /* For positioning the overlay */
  padding: 16px 24px;

  &__title {
    @include inter-font($size: 18px, $bolded: true, $color: var(--main-text-color));
    margin: 0 0 2px 0;
  }

  &__subtitle {
    @include inter-font($size: 14px, $color: var(--secondary-text-color));
    margin: 0 0 24px 0;
  }

  &__loading {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 150px;
  }

  &__list {
    background-color: var(--secondary-bg-color);
    border-radius: 8px;
  }

  &__scrollable-container {
    &--scrollable {
      max-height: 280px; /* Approximately 3.5 team member items */
      overflow-y: auto;

      /* Custom scrollbar styling */
      &::-webkit-scrollbar {
        width: 6px;
      }

      &::-webkit-scrollbar-track {
        background: rgba(0, 0, 0, 0.05);
        border-radius: 3px;
      }

      &::-webkit-scrollbar-thumb {
        background: rgba(0, 0, 0, 0.2);
        border-radius: 3px;

        &:hover {
          background: rgba(0, 0, 0, 0.3);
        }
      }

      /* Firefox scrollbar styling */
      scrollbar-width: thin;
      scrollbar-color: rgba(0, 0, 0, 0.2) rgba(0, 0, 0, 0.05);
    }
  }

  &__add-btn {
    margin: 8px auto;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: var(--main-text-color);
    background-color: var(--terziary-btn-color);
    padding: 6px 12px;
    transition: background-color 0.3s;
    border-radius: 8px;
    @include inter-font($size: 13px, $bolded: true, $color: var(--main-text-color));
  }
}
</style>
